import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, MoveRight, Save } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import estoqueMovimentacaoService, {
  type EstoqueMovimentacaoInput,
  type EstoqueMovimentacaoTipo,
} from '@/services/estoqueMovimentacao';
import produtoService, { type Produto } from '@/services/produto';
import loteService, { type Lote } from '@/services/lote';
import localService, { type Local } from '@/services/local';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

type ApiError = {
  message?: string;
  errors?: Record<string, string[]>;
};

const TIPO_LABELS: Record<EstoqueMovimentacaoTipo, string> = {
  ENTRADA: 'Entrada',
  SAIDA: 'Saída',
  TRANSFERENCIA: 'Transferência',
  AJUSTE: 'Ajuste',
  PRODUCAO: 'Produção',
  DEVOLUCAO: 'Devolução',
};

function agoraLocal(): string {
  const agora = new Date();
  agora.setMinutes(agora.getMinutes() - agora.getTimezoneOffset());
  return agora.toISOString().slice(0, 16);
}

function toDatetimeLocal(value?: string | null) {
  if (!value) return '';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return value.slice(0, 16);
  data.setMinutes(data.getMinutes() - data.getTimezoneOffset());
  return data.toISOString().slice(0, 16);
}

function getApiErrors(error: unknown, fallback: string) {
  const data = (error as { response?: { data?: ApiError } }).response?.data;
  if (data?.errors) {
    return Object.entries(data.errors).flatMap(([field, messages]) =>
      messages.map((message) => `${field}: ${message}`)
    );
  }
  return [data?.message || fallback];
}

const FORM_VAZIO: EstoqueMovimentacaoInput = {
  tipo: 'ENTRADA',
  produto_id: '',
  lote_id: '',
  quantidade: 0,
  data_movimento: agoraLocal(),
  origem_id: '',
  destino_id: '',
  documento: '',
  observacoes: '',
};

export default function MovimentacaoForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const visualizando = location.pathname.endsWith('/visualizar');
  const [form, setForm] = useState<EstoqueMovimentacaoInput>(FORM_VAZIO);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);

  const produtosValidos = useMemo(() => produtos.filter((produto) => getUuidCandidate(produto)), [produtos]);
  const lotesValidos = useMemo(() => lotes.filter((lote) => getUuidCandidate(lote)), [lotes]);
  const locaisValidos = useMemo(() => locais.filter((local) => getUuidCandidate(local)), [locais]);

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
    localService.listar().then(setLocais).catch(console.error);
  }, []);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    estoqueMovimentacaoService.buscarPorId(id)
      .then((data) => {
        setForm({
          tipo: data.tipo,
          produto_id: data.produto_id,
          lote_id: data.lote_id || '',
          quantidade: data.quantidade,
          data_movimento: toDatetimeLocal(data.data_movimento),
          origem_id: data.origem_id || '',
          destino_id: data.destino_id || '',
          documento: data.documento || '',
          op_id: data.op_id || '',
          responsavel_id: data.responsavel_id || '',
          observacoes: data.observacoes || '',
        });
        if (data.produto_id) {
          loteService.listar({ produto: data.produto_id }).then(setLotes).catch(console.error);
        }
      })
      .catch((error) => {
        console.error('Erro ao carregar movimentação', error);
        setErros(['Não foi possível carregar a movimentação.']);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const onProdutoChange = async (produtoId: string) => {
    setForm((prev) => ({ ...prev, produto_id: produtoId, lote_id: '' }));
    if (!produtoId) {
      setLotes([]);
      return;
    }

    try {
      setLotes(await loteService.listar({ produto: produtoId, status: 'LIVRE' }));
    } catch (error) {
      console.error('Erro ao carregar lotes do produto', error);
      setLotes([]);
    }
  };

  const salvar = async () => {
    if (visualizando) return;

    const errosFormulario: string[] = [];
    if (!form.tipo) errosFormulario.push('Tipo: selecione o tipo da movimentação.');
    if (!isUuid(form.produto_id)) errosFormulario.push('Produto: selecione um produto válido.');
    if (!form.quantidade || Number(form.quantidade) <= 0) errosFormulario.push('Quantidade: informe um valor maior que zero.');
    if (form.tipo === 'TRANSFERENCIA' && !isUuid(form.origem_id)) errosFormulario.push('Origem: selecione um local de origem válido.');
    if (form.tipo === 'TRANSFERENCIA' && !isUuid(form.destino_id)) errosFormulario.push('Destino: selecione um local de destino válido.');

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      await estoqueMovimentacaoService.criar({
        ...form,
        lote_id: form.lote_id || null,
        origem_id: form.origem_id || null,
        destino_id: form.destino_id || null,
        documento: form.documento || null,
        observacoes: form.observacoes || null,
      });
      navigate('/producao/estoque/movimentacoes');
    } catch (error) {
      console.error('Erro ao registrar movimentação', error);
      setErros(getApiErrors(error, 'Não foi possível salvar a movimentação. Verifique os dados e tente novamente.'));
    } finally {
      setSalvando(false);
    }
  };

  const title = visualizando ? 'Visualizar Movimentação' : 'Nova Movimentação';
  const description = visualizando
    ? 'Consulte os dados do lançamento de estoque.'
    : 'Registre entradas, saídas, ajustes e transferências de estoque.';

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader
        icon={<MoveRight size={21} />}
        title={title}
        description={description}
        actions={
          <Button type="button" variant="ghost" onClick={() => navigate('/producao/estoque/movimentacoes')}>
            <ArrowLeft size={16} />
            Voltar
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível salvar a movimentação.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <fieldset disabled={loading || visualizando} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header"><h2>Dados da movimentação</h2></div>
            <div className="nx-sngpc-page-form-grid">
              <Field label="Tipo *" htmlFor="mov-tipo">
                <Select
                  id="mov-tipo"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as EstoqueMovimentacaoTipo })}
                  required
                >
                  {(Object.keys(TIPO_LABELS) as EstoqueMovimentacaoTipo[]).map((tipo) => (
                    <option key={tipo} value={tipo}>{TIPO_LABELS[tipo]}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Produto *" htmlFor="mov-produto" className="nx-sngpc-field--full">
                <Select
                  id="mov-produto"
                  value={form.produto_id}
                  onChange={(e) => void onProdutoChange(e.target.value)}
                  required
                >
                  <option value="">Selecione</option>
                  {produtosValidos.map((produto) => (
                    <option key={produto.id} value={getUuidCandidate(produto) || ''}>
                      {produto.codigo} - {produto.descricao}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Lote" htmlFor="mov-lote">
                <Select
                  id="mov-lote"
                  value={form.lote_id || ''}
                  onChange={(e) => setForm({ ...form, lote_id: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {lotesValidos.map((lote) => (
                    <option key={lote.id} value={getUuidCandidate(lote) || ''}>{lote.numero_lote}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Quantidade *" htmlFor="mov-quantidade">
                <DecimalInput
                  id="mov-quantidade"
                  value={form.quantidade}
                  onValueChange={(value) => setForm({ ...form, quantidade: value ?? 0 })}
                  required
                />
              </Field>
              <Field label="Data/Hora" htmlFor="mov-data">
                <Input
                  id="mov-data"
                  type="datetime-local"
                  value={form.data_movimento || ''}
                  onChange={(e) => setForm({ ...form, data_movimento: e.target.value })}
                />
              </Field>
              <Field label={`Origem ${form.tipo === 'TRANSFERENCIA' ? '*' : ''}`} htmlFor="mov-origem">
                <Select
                  id="mov-origem"
                  value={form.origem_id || ''}
                  onChange={(e) => setForm({ ...form, origem_id: e.target.value })}
                  required={form.tipo === 'TRANSFERENCIA'}
                >
                  <option value="">Selecione</option>
                  {locaisValidos.map((local) => (
                    <option key={local.id} value={getUuidCandidate(local) || ''}>
                      {local.codigo} - {local.descricao}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={`Destino ${form.tipo === 'TRANSFERENCIA' ? '*' : ''}`} htmlFor="mov-destino">
                <Select
                  id="mov-destino"
                  value={form.destino_id || ''}
                  onChange={(e) => setForm({ ...form, destino_id: e.target.value })}
                  required={form.tipo === 'TRANSFERENCIA'}
                >
                  <option value="">Selecione</option>
                  {locaisValidos.map((local) => (
                    <option key={local.id} value={getUuidCandidate(local) || ''}>
                      {local.codigo} - {local.descricao}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Documento" htmlFor="mov-documento">
                <Input
                  id="mov-documento"
                  value={form.documento || ''}
                  onChange={(e) => setForm({ ...form, documento: e.target.value })}
                />
              </Field>
              <Field label="Observações" htmlFor="mov-observacoes" className="nx-sngpc-field--full">
                <Textarea
                  id="mov-observacoes"
                  value={form.observacoes || ''}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3}
                />
              </Field>
            </div>
          </section>
        </fieldset>
      </Card>

      {visualizando ? (
        <FormStickyFooter>
          <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <Button type="button" variant="ghost" onClick={() => navigate('/producao/estoque/movimentacoes')}>
              <ArrowLeft size={16} />
              Voltar
            </Button>
          </div>
        </FormStickyFooter>
      ) : (
        <FormStickyFooter
          cancelLabel="Cancelar"
          onCancel={() => navigate('/producao/estoque/movimentacoes')}
          saveLabel="Salvar"
          loading={salvando}
          saveIcon={<Save size={16} />}
        />
      )}
    </form>
  );
}
